<!-- SectionIntro360Sphere.vue -->
<script setup lang="ts">
import { toRefs } from 'vue'

import iconServiceAssessment from '@/components/icons/service/assessment.vue'
import iconServiceSolutions from '@/components/icons/service/solutions.vue'
import iconServiceImplementation from '@/components/icons/service/implementation.vue'
import iconServiceSupport from '@/components/icons/service/support.vue'
import iconServiceImprovment from '@/components/icons/service/improvement.vue'

type Phase = 'node' | 'link' | 'summary'

interface Service {
  index: number
  id: string
  icon: string
  label: string
  shortLabel: string
  description: string
  color: string
}

const props = defineProps<{
  services: Service[]
  activeIndex: number
  completedCount: number
  completedSegments: number
  phase: Phase
  progressKey: number
  linkFromIndex: number
}>()

const emit = defineEmits<{ (event: 'select-service', id: string): void }>()

const { services, activeIndex, completedCount, completedSegments, phase, progressKey, linkFromIndex } = toRefs(props)

const R = 140
const NODE_R = 50
const DISC_R_ACTIVE = 46
const DISC_R_INACTIVE = 30
const ICON_INACTIVE_ALPHA = 0.35

function toRad(deg: number) {
  return (deg * Math.PI) / 180
}

function polar(cx: number, cy: number, radius: number, angleDeg: number) {
  const a = toRad(angleDeg)
  return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) }
}

function nodeAngle(i: number) {
  const step = 360 / services.value.length
  return -90 + i * step
}

function describeArc(cx: number, cy: number, radius: number, startAngle: number, endAngle: number) {
  let s = startAngle
  let e = endAngle
  if (e <= s) e += 360
  const start = polar(cx, cy, radius, s)
  const end = polar(cx, cy, radius, e)
  const largeArc = (e - s) <= 180 ? 0 : 1
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

function segmentPath(i: number) {
  const start = nodeAngle(i)
  const end = nodeAngle((i + 1) % services.value.length)
  return describeArc(0, 0, R, start, end)
}

function getServicePosition(index: number) {
  const angle = nodeAngle(index)
  const { x, y } = polar(0, 0, R, angle)
  return { x, y }
}

function handleSelect(id: string) {
  emit('select-service', id)
}

function hexToRgba(hex: string, alpha: number) {
  const normalized = hex.replace('#', '')
  const bigint = Number.parseInt(normalized.length === 3
    ? normalized.split('').map(ch => ch + ch).join('')
    : normalized, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function iconColor(service: Service, index: number) {
  if (index === activeIndex.value || index < completedCount.value) return '#ffffff'
  return hexToRgba(service.color, ICON_INACTIVE_ALPHA)
}
</script>

<template>
  <div class="service-circle-container relative flex items-center justify-center w-full ">
    <div class="service-circle absolute top-50% left-50% -translate-x-50% -translate-y-50% w-100% h-100% ">
      <svg viewBox="-200 -200 400 400"
        class="w-100% tm:w-100%! h-auto absolute top-50% left-50% -translate-x-50% -translate-y-50% max-h-100% t:max-h-450px! ds:max-h-100%">
        <circle cx="0" cy="0" :r="R" fill="none" stroke="currentColor" stroke-width="1" opacity="0.2"
          class="text-gray-400" />

        <template v-for="(s, i) in services" :key="'seg-complete-' + s.id">
          <path v-if="i < completedSegments" :d="segmentPath(i)" :stroke="services[i].color" stroke-width="3" fill="none"
            stroke-linecap="round" />
        </template>

        <path v-if="phase === 'link'" :key="'seg-link-' + progressKey" :d="segmentPath(linkFromIndex)"
          :stroke="services[linkFromIndex].color" stroke-width="3" fill="none" stroke-linecap="round" pathLength="100"
          class="outer-link-progress" />

        <g v-for="(service, index) in services" :key="service.id">
          <circle v-if="index === activeIndex && phase === 'node'"
            :key="'node-progress-' + service.id + '-' + progressKey" :cx="getServicePosition(index).x"
            :cy="getServicePosition(index).y" :r="NODE_R" fill="none" :stroke="service.color" stroke-width="3"
            pathLength="100" stroke-dasharray="100" stroke-dashoffset="100" class="node-progress" />

          <circle v-if="index < completedCount" :cx="getServicePosition(index).x" :cy="getServicePosition(index).y"
            :r="NODE_R" fill="none" :stroke="service.color" stroke-width="2" opacity="1" />

          <circle :cx="getServicePosition(index).x" :cy="getServicePosition(index).y"
            :fill="(index < completedCount || index === activeIndex) ? service.color : 'white'"
            :r="(index < completedCount || index === activeIndex) ? DISC_R_ACTIVE : DISC_R_INACTIVE"
            :stroke="(index < completedCount || index === activeIndex) ? service.color : 'currentColor'" stroke-width="1"
            class="cursor-pointer transition-all"
            :style="{ transition: 'r 0.3s ease, fill 0.3s ease, stroke 0.3s ease' }"
            :class="{ 'text-gray-300': !(index < completedCount || index === activeIndex), 'shadow-lg': index === activeIndex }"
            @click="handleSelect(service.id)" />
        </g>

        <foreignObject v-for="(service, index) in services" :key="'icon-' + service.id"
          :x="getServicePosition(index).x - 24" :y="getServicePosition(index).y - 24" width="48" height="48"
          class="cursor-pointer" @click="handleSelect(service.id)">
          <div class="flex items-center justify-center w-full h-full pointer-events-auto sec-circle-icons"
            :style="{
              transform: (index < completedCount || index === activeIndex) ? 'scale(1)' : 'scale(0.7)',
              transition: 'transform 0.3s ease'
            }">
            <iconServiceAssessment v-if="service.id === 'assessment'" class="w-95%! transition-all assessment"
              :color="iconColor(service, index)" />
            <iconServiceSolutions v-if="service.id === 'solutions'" class="w-95%! transition-all solutions"
              :color="iconColor(service, index)" />
            <iconServiceImplementation v-if="service.id === 'implementation'" class="w-95%! transition-all implementation"
              :color="iconColor(service, index)" />
            <iconServiceSupport v-if="service.id === 'support'" class="w-95%! transition-all support"
              :color="iconColor(service, index)" />
            <iconServiceImprovment v-if="service.id === 'improvement'" class="w-95%! transition-all improvement"
              :color="iconColor(service, index)" />
          </div>
        </foreignObject>

        <foreignObject x="-60" y="-60" width="120" height="120" class="pointer-events-none">
          <div class="flex items-center justify-center w-full h-full">
            <img src="/icon.png" alt="Altracare" class="w-60% h-60%">
          </div>
        </foreignObject>
      </svg>
    </div>
  </div>
</template>

<style scoped lang="scss">
.service-circle-container {
  min-height: 400px;

  .service-circle {
    width: 100%;
  }
}

.node-progress {
  animation: nodeStrokeReveal linear forwards;
  animation-duration: 3s;
}

@keyframes nodeStrokeReveal {
  from {
    stroke-dashoffset: 100;
  }

  to {
    stroke-dashoffset: 0;
  }
}

.outer-link-progress {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: segStrokeReveal 0.2s linear forwards;
  will-change: stroke-dashoffset;
}

@keyframes segStrokeReveal {
  from {
    stroke-dashoffset: 100;
  }

  to {
    stroke-dashoffset: 0;
  }
}
</style>
